import type MathsStudentSummaryTableRoute from 'teacher-ui-client/reporting/ui-scope/maths/student-summary/table/route'
import RouteTemplate from 'ember-route-template'
import { ReportingTeacherUiHeader } from 'teacher-ui-client/components/reporting/teacher-ui-header'
import { ReportingStudentSummaryControls } from 'teacher-ui-client/components/reporting/student-summary-controls'
import { StudentDashboardTile } from 'teacher-ui-client/components/tiles/student-dashboard'
import BasicTable from 'teacher-ui-client/components/stable-table/basic-table'
import CalendarIconCell from 'teacher-ui-client/components/stable-table/basic-cells/calendar-icon'
import TooltipCell from 'teacher-ui-client/components/stable-table/basic-cells/tooltip'
import { t, type IntlService } from 'ember-intl'
import { ExportType } from 'teacher-ui-client/components/reporting/export-select'
import { array } from '@ember/helper'
import Component from '@glimmer/component'
import { service } from '@ember/service'
import type MathsDrivingTestsService from 'teacher-ui-client/services/maths-driving-tests'
import { formatValue } from 'teacher-ui-client/utils/format-value'
import { unixTimeToCalendarIconArgs } from 'teacher-ui-client/utils/unix-time-to-calendar-icon'
import type { CellObject } from 'teacher-ui-client/components/stable-table/basic-table/cell'
import { ThemeKeys } from 'teacher-ui-client/utils/themes'
import type {
  AssignmentsRecentActivityItemMs,
  AssignmentsRecentActivityDrivingTestItemMs,
  DrivingTestsCategories,
} from 'teacher-ui-client/utils/student-events/fetchers/summary'
import { Precinct } from 'teacher-ui-client/utils/precincts'
import type LessonInfoService from 'teacher-ui-client/services/lesson-info'
import { isPresent } from '@ember/utils'

interface Signature {
  Args: {
    model: ModelFor<MathsStudentSummaryTableRoute>
  }
}
export class UiScopeMathsStudentSummaryTablePage extends Component<Signature> {
  @service intl!: IntlService
  @service mathsDrivingTests!: MathsDrivingTestsService
  @service lessonInfo!: LessonInfoService

  get pageTitle() {
    return `Student results ${this.args.model.student.fullName}`
  }

  get summary() {
    return this.args.model.summary?.[0]
  }

  readonly tileTheme: ThemeKeys = ThemeKeys.green

  get emptyText() {
    return this.intl.t('reporting.maths.studentSummary.emptyText')
  }

  get lessonsProgressData() {
    const columns = [{ cellClassNames: 'p-3 text-left' }, { cellClassNames: 'w-24 text-center' }]

    const initialText = this.intl.t('reporting.maths.studentSummary.mathsLessons.initialLesson')
    const highestText = this.intl.t('reporting.maths.studentSummary.mathsLessons.highestLesson')
    const totalText = this.intl.t('reporting.maths.studentSummary.mathsLessons.totalLessons')
    const totalMasteredText = this.intl.t('reporting.maths.studentSummary.mathsLessons.totalLessonsMastered')

    const totalMasteredTooltip = this.intl.t('reporting.maths.studentSummary.mathsLessons.totalLessonsMasteredTooltip')

    const rows = [
      { cells: [{ value: initialText }, { value: formatValue(this.summary?.lessons.initial_lesson) }] },
      { cells: [{ value: highestText }, { value: formatValue(this.summary?.lessons.highest_lesson) }] },
      {
        cells: [{ value: totalText }, { value: formatValue(this.summary?.lessons.total_lessons) }],
        classNames: 'font-semibold',
      },
      {
        cells: [
          {
            component: TooltipCell,
            classNames: 'bg-ms-green-100 border-r border-white',
            value: totalMasteredText,
            tooltipValue: totalMasteredTooltip,
          },
          { value: formatValue(this.summary?.lessons.total_lessons_mastered), classNames: 'bg-ms-green-100' },
        ],
        classNames: 'font-semibold',
      },
    ]

    return { columns, rows }
  }

  /**
   * Transforms data for the 'Lessons' placement tests table
   */

  get lessonsPlacementTestData() {
    const columns = [
      {
        cellClassNames: 'py-3 w-16 text-center text-ms-green-300',
        cellComponent: CalendarIconCell,
      },
      {},
    ]

    const data = this.summary?.lessons_placement_tests || []

    const rows = data.map((test) => {
      const calendarCell = unixTimeToCalendarIconArgs(test.created_at, this.intl)
      const isLessonSkipped = test.skipped_test
      const lessonNumber = test.lesson
      let lessonText = this._getLessonTextFromNumber(lessonNumber)

      if (isLessonSkipped) {
        lessonText = this.intl.t('reporting.placementTest.skipped')
      }

      return {
        cells: [calendarCell, { value: lessonText }],
      }
    })

    return { columns, rows }
  }

  /**
   * @memberOf MathsStudentSummaryTable
   *
   * Transforms data for the 'Mathseeds Lessons' recent activity table
   */
  get lessonsActivityData() {
    const columns = [
      {
        cellClassNames: 'py-3 w-16 text-center text-ms-green-300',
        cellComponent: CalendarIconCell,
      },
      {},
      { cellClassNames: 'w-24 text-center' },
    ]

    const lessonsRecentActivity = this.summary?.lessons?.recent_activity || []

    const rows = lessonsRecentActivity.map((activity) => {
      const calendarCell = unixTimeToCalendarIconArgs(activity.created_at, this.intl)
      const lessonNumber = activity.lesson
      const lessonText = this._getLessonTextFromNumber(lessonNumber)

      return {
        cells: [calendarCell, { value: lessonText }, { value: formatValue(activity.score, { append: '%' }) }],
      }
    })

    return { columns, rows }
  }

  get areasOfWeaknessRecentActivityData() {
    const columns = [
      {
        cellClassNames: 'py-3 w-16 text-center text-ms-green-300',
        cellComponent: CalendarIconCell,
      },
      {},
      { cellClassNames: 'w-24 text-center' },
    ]

    const areasOfWeaknessRecentActivity = this.summary?.lessons?.areas_of_weakness || []

    const rows = areasOfWeaknessRecentActivity.map((activity) => {
      const calendarCell = unixTimeToCalendarIconArgs(activity.created_at, this.intl)
      const lessonNumber = activity.lesson
      const lessonText = this._getLessonTextFromNumber(lessonNumber)

      return {
        cells: [calendarCell, { value: lessonText }, { value: formatValue(activity.score, { append: '%' }) }],
      }
    })

    return { columns, rows }
  }

  /**
   * @memberOf MathsStudentSummaryTable
   *
   * Transforms data for the 'Assignments' recent activity table
   */

  get assignmentsData() {
    const columns = [
      {
        cellClassNames: 'py-3 w-16 text-center text-ms-green-300',
        cellComponent: CalendarIconCell,
      },
      {},
      { cellClassNames: 'w-24 text-center' },
    ]

    const assignmentsRecentActivity = this.summary?.assignments?.recent_activity || []

    const rows = assignmentsRecentActivity.map((activity) => {
      const calendarCell = unixTimeToCalendarIconArgs(activity.created_at, this.intl)
      const assignmentText = this._formatAssignmentRow(activity)

      return {
        cells: [calendarCell, { value: assignmentText }, { value: formatValue(activity.score, { append: '%' }) }],
      }
    })

    return { columns, rows }
  }

  /**
   * @memberOf MathsStudentSummaryTable
   *
   * Returns a set of rows, columns and footer rows, that will be rendered into the driving tests panel as a progress
   * table
   */
  get drivingTestsProgressData() {
    const drivingTestGradeData = this.summary?.driving_tests?.totals_by_grade || []

    // Massage the data so we have easy access to unique categories, driving test grades, and their data points

    // Note: When a set is iterated over (as they will be to create the data rows below), the order is determined by
    // their insertion order. In this case, the insertion order is dependent on their order in the summary data.
    const categorySet: Set<string> = new Set()
    const drivingTestGradeSet: Set<number> = new Set()
    const dataPoints: Record<string, DrivingTestsCategories> = {}

    drivingTestGradeData.forEach((gradeData) => {
      const dtGrade = gradeData.driving_test_grade
      drivingTestGradeSet.add(dtGrade)

      gradeData.categories.forEach((gradeCategoryData) => {
        const { category } = gradeCategoryData
        categorySet.add(category)

        const dataPointKey = `${category}_${dtGrade}`

        if (!dataPoints[dataPointKey]) {
          dataPoints[dataPointKey] = gradeCategoryData
        }
      })
    })

    const categories = Array.from(categorySet)
    const drivingTestGrades = Array.from(drivingTestGradeSet)

    // Columns - one per dt grade

    const categoryColumn = { cellClassNames: 'p-3 text-left' }

    const dtColumns = drivingTestGradeData.map((grade) => {
      const drivingTestGradeText = this._formatDrivingTestGradeLabel(grade.driving_test_grade)
      return { cellClassNames: 'p-3 text-center', value: drivingTestGradeText }
    })

    const columns = [categoryColumn, ...dtColumns]

    // Rows - one per category

    const rows: CellObject[] = categories.map((category) => {
      const categoryText = this.intl.t(`reporting.maths.studentSummary.drivingTests.categories.${category}`)
      const categoryCell = { value: categoryText }

      const dtGradeCells = drivingTestGrades.map((dtGrade) => {
        const dataPoint = dataPoints[`${category}_${dtGrade}`]
        if (dataPoint) {
          const { completed, total_available_tests } = dataPoint
          return { value: `${completed}/${total_available_tests}` }
        } else {
          return '-'
        }
      })

      return { cells: [categoryCell, ...dtGradeCells] }
    })

    // Total row
    const totalCategoryText = this.intl.t('reporting.maths.studentSummary.drivingTests.totalCategory')
    const categoryTotalCell = { value: totalCategoryText, classNames: 'border-r border-white p-3 text-left' }

    const dtGradeTotalCells = Array.from(drivingTestGrades).map((dtGrade) => {
      const [completed, available] = Array.from(categories).reduce(
        (result, category) => {
          const [totalCompleted, totalAvailable] = result
          const dataPoint = dataPoints[`${category}_${dtGrade}`]
          if (dataPoint) {
            return [totalCompleted + dataPoint.completed, totalAvailable + dataPoint.total_available_tests]
          } else {
            return result
          }
        },
        [0, 0],
      )
      const noTotals = completed === 0 && available === 0
      const value = noTotals ? 0 : `${completed}/${available}`
      return { value, classNames: 'border-r border-white p-3 text-center' }
    })

    const totalRow = {
      classNames: 'bg-ms-green-50 font-semibold',
      cells: [categoryTotalCell, ...dtGradeTotalCells],
    }

    // if there are no dt grade columns, we want to show a zero value
    if (totalRow.cells.length === 1) {
      totalRow.cells.push({ value: 0, classNames: 'border-r border-white p-3 text-center' })
    }

    const footerRows = [totalRow]

    return { columns, rows, footerRows }
  }

  /**
   * @memberOf MathsStudentSummaryTable
   *
   * Transforms data for the 'Driving Tests' recent activity table
   */

  get drivingTestsActivityData() {
    const columns = [
      {
        cellClassNames: 'py-3 w-16 text-center text-ms-green-300',
        cellComponent: CalendarIconCell,
      },
      {},
      {},
      {},
      { cellClassNames: 'w-24 text-center' },
    ]

    const drivingTestsRecentActivity = this.summary?.driving_tests?.recent_activity || []

    const rows = drivingTestsRecentActivity.map((activity) => {
      const calendarCell = unixTimeToCalendarIconArgs(activity.created_at, this.intl)
      const drivingTestGradeText = this._formatDrivingTestGradeLabel(activity.driving_test_grade)
      const categoryText = this.intl.t(`reporting.maths.studentSummary.drivingTests.categories.${activity.category}`)
      const quizText = this.intl.t('reporting.maths.studentSummary.drivingTests.quizNumber', { number: activity.quiz })

      let score = undefined
      if (activity.correct && activity.total) {
        const correct = Number(activity.correct)
        const total = Number(activity.total)
        if (Number.isInteger(correct) && Number.isInteger(total) && correct <= total && total > 0) {
          score = Math.round((correct / total) * 100)
        }
      }

      return {
        cells: [
          calendarCell,
          { value: drivingTestGradeText },
          { value: categoryText },
          { value: quizText },
          { value: formatValue(score, { append: '%' }) },
        ],
      }
    })

    return { columns, rows }
  }

  /**
   * @memberOf MathsStudentSummaryTable
   *
   * Transforms data for the 'Map Quizzes' progress table
   */
  get mapQuizzesProgressData() {
    const columns = [{ cellClassNames: 'p-3 text-left' }, { cellClassNames: 'w-24 text-center' }]

    const numberQuizzesTaken = this.intl.t('reporting.maths.studentSummary.mapQuizzes.numberQuizzesTaken')
    const averageQuizScore = this.intl.t('reporting.maths.studentSummary.mapQuizzes.averageQuizScore')
    const totalAttempts = this.intl.t('reporting.maths.studentSummary.mapQuizzes.totalAttempts')

    const rows = [
      { cells: [{ value: numberQuizzesTaken }, { value: formatValue(this.summary?.assessments.quizzes_taken) }] },
      {
        classNames: 'bg-ms-green-100 font-semibold',
        cells: [
          { classNames: 'border-r border-white', value: averageQuizScore },
          { value: formatValue(this.summary?.assessments.average_score, { append: '%' }) },
        ],
      },
      {
        classNames: 'font-semibold',
        cells: [{ value: totalAttempts }, { value: formatValue(this.summary?.assessments.attempts) }],
      },
    ]

    return { columns, rows }
  }

  /**
   * @memberOf MathsStudentSummaryTable
   *
   * Transforms data for the 'Map Quizzes' recent activity table
   */
  get mapQuizzesRecentActivityData() {
    const columns = [
      {
        cellClassNames: 'py-3 w-16 text-center text-ms-green-300',
        cellComponent: CalendarIconCell,
      },
      {},
      { cellClassNames: 'w-24 text-center' },
    ]

    const mapQuizzesRecentActivity = this.summary?.assessments?.recent_activity || []

    const rows = mapQuizzesRecentActivity.map((activity) => {
      const calendarCell = unixTimeToCalendarIconArgs(activity.created_at, this.intl)
      const mapText = this.intl.t('reporting.maths.studentSummary.mapQuizzes.activityRowQuiz', {
        mapNumber: activity.map_number,
      })
      const lessonRange = this.lessonInfo
        .lessonsFor(Precinct.MS_LESSONS)
        .filter((lesson) => lesson.map === activity.map_number)
        .map((lesson) => lesson.lesson_position)
        .filter(isPresent)

      let assessingLessons = ''
      if (lessonRange && lessonRange.length > 0) {
        const lessonMin = lessonRange[0]
        const lessonMax = lessonRange[lessonRange.length - 1]
        assessingLessons = this.intl.t('reporting.maths.studentSummary.mapQuizzes.activityRowLessons', {
          lessonMin,
          lessonMax,
        })
      }

      const activityText = `${mapText}: ${assessingLessons}`

      return {
        cells: [calendarCell, { value: activityText }, { value: formatValue(activity.score, { append: '%' }) }],
      }
    })

    return { columns, rows }
  }

  /**
   * @memberOf MathsStudentSummaryTable
   *
   * Transforms data for 'Certificates Earned' table
   */
  get certificatesEarnedData() {
    const goldText = this.intl.t('reporting.maths.studentSummary.certificatesEarned.gold')
    const silverText = this.intl.t('reporting.maths.studentSummary.certificatesEarned.silver')
    const bronzeText = this.intl.t('reporting.maths.studentSummary.certificatesEarned.bronze')
    const columns = [
      { value: goldText, cellClassNames: 'py-3 text-center' },
      { value: silverText, cellClassNames: 'py-3 text-center' },
      { value: bronzeText, cellClassNames: 'py-3 text-center' },
    ]

    const rows = [
      {
        cells: [
          { value: formatValue(this.summary?.awards.gold) },
          { value: formatValue(this.summary?.awards.silver) },
          { value: formatValue(this.summary?.awards.bronze) },
        ],
      },
    ]

    return { columns, rows }
  }

  get mentalMinuteProgressData() {
    const columns = [
      { cellClassNames: 'py-3 px-6 font-semibold' },
      { cellClassNames: 'text-center w-40 font-semibold' },
    ]

    const mentalMinuteBadgesEarned = this.summary?.mental_minute?.total_badges_earned

    const text = this.intl.t('reporting.maths.studentSummary.mentalMinute.totalBadgesEarned')
    const row = { cells: [{ value: text }, { value: formatValue(mentalMinuteBadgesEarned) }] }

    const rows = [row]

    return { columns, rows }
  }

  get mentalMinuteRecentActivityData() {
    const columns = [
      {
        cellClassNames: 'py-3 w-16 text-center text-ms-green-300',
        cellComponent: CalendarIconCell,
      },
      {},
      { cellClassNames: 'w-40 text-center' },
    ]

    const mentalMinuteRecentActivity = this.summary?.mental_minute?.recent_activity || []

    const rows = mentalMinuteRecentActivity.map((activity) => {
      const calendarCell = unixTimeToCalendarIconArgs(activity.created_at, this.intl)
      const lessonNumber = activity.lesson
      const { category: lessonCategory, name: lessonName } =
        this.lessonInfo
          .lessonsFor(Precinct.MS_MENTAL_MINUTE)
          .find((lesson) => lesson.lesson_position === lessonNumber) ?? {}
      const lessonText = `${lessonCategory} ${lessonNumber}: ${lessonName}`

      return {
        cells: [calendarCell, { value: lessonText }, { value: activity.score }],
      }
    })

    return { columns, rows }
  }

  _getLessonTextFromNumber(lessonNumber: number) {
    const lessonName = this.lessonInfo
      .lessonsFor(Precinct.MS_LESSONS)
      .find((lesson) => lesson.lesson_position === lessonNumber)?.name
    return `Lesson ${lessonNumber}: ${lessonName}`
  }

  _formatDrivingTestGradeLabel(drivingTestGradePosition: number) {
    const drivingTest = this.mathsDrivingTests.findByDrivingTestGrade(drivingTestGradePosition)
    return drivingTest?.name
  }

  _formatAssignmentRow(assignment: AssignmentsRecentActivityItemMs | AssignmentsRecentActivityDrivingTestItemMs) {
    const { precinct, quiz, lesson } = assignment

    if (precinct === 'driving_tests') {
      const drivingTestCategory = 'driving_test_category' in assignment ? assignment.driving_test_category : ''
      const drivingTestGrade = 'driving_test_grade' in assignment ? assignment.driving_test_grade : undefined

      const drivingTest = this.mathsDrivingTests.findByDrivingTestGrade(Number(drivingTestGrade))
      const drivingTestGradeText = drivingTest?.name
      const gradePosition = drivingTest?.gradeSetPosition
      const categoryName = this.intl.t(`driving_tests.maths.categories.${drivingTestCategory}`)
      const name = this.lessonInfo
        .lessonsFor(Precinct.MS_DRIVING_TESTS)
        .find(
          (mathLesson) =>
            mathLesson.grade_lesson_number === quiz &&
            mathLesson.grade_position === gradePosition &&
            mathLesson.category === drivingTestCategory,
        )?.name
      return `${drivingTestGradeText} ${categoryName} Test ${quiz}: ${name}`
    } else {
      const name = this.lessonInfo
        .lessonsFor(Precinct.MS_LESSONS)
        .find((mathLesson) => mathLesson.lesson_position === lesson)?.name
      return `${name} (Lesson ${lesson})`
    }
  }
  <template>
    <ReportingTeacherUiHeader @title={{this.pageTitle}} />

    <ReportingStudentSummaryControls
      @students={{@model.students}}
      @student={{@model.student}}
      @routePrefix="reporting.ui-scope.maths.student-summary"
      @exportOptions={{array ExportType.CSV ExportType.print}}
      @isDataPresent={{Boolean @model.students}}
      @studentRouteName="reporting.ui-scope.maths.student-summary.table"
      @allStudentsRouteName="reporting.ui-scope.maths.dashboard.table"
    />

    <div class="mx-auto flex max-w-(--breakpoint-lg) flex-wrap print:block">
      <div class="w-full lg:w-1/2 print:w-full">
        <StudentDashboardTile
          data-test-student-dashboard-tile="mathseeds-lessons"
          class="m-3"
          @theme={{this.tileTheme}}
          @title={{t "reporting.maths.studentSummary.mathsLessons.title"}}
          @icon="phosphor-share-network-fill"
        >
          {{! Section header }}
          <div class="border-ms-green-150 flex items-end justify-between border-b-4 font-bold print:border-black">
            <span class="px-3 pt-6 pb-3">
              {{t "reporting.maths.studentSummary.mathsLessons.progressTitle"}}
            </span>
          </div>

          {{! Table }}
          <BasicTable
            exportable="{{t 'reporting.maths.studentSummary.mathsLessons.title'}} - {{t
              'reporting.maths.studentSummary.mathsLessons.progressTitle'
            }}"
            class="w-full"
            @columns={{this.lessonsProgressData.columns}}
            @rows={{this.lessonsProgressData.rows}}
            as |bt|
          >
            <bt.body as |b|>
              <b.row class="border-ms-green-150 border-b print:border-black" />
            </bt.body>
          </BasicTable>

          {{! Section header }}
          <div class="border-ms-green-150 mt-3 flex items-end justify-between border-b-4 font-bold print:border-black">
            <span class="px-3 pt-6 pb-3">{{t "reporting.maths.studentSummary.mathsLessons.placementTestsTitle"}}</span>
          </div>

          {{! Table }}
          <BasicTable
            exportable="{{t 'reporting.maths.studentSummary.mathsLessons.title'}} - {{t
              'reporting.maths.studentSummary.mathsLessons.placementTestsTitle'
            }}"
            class="w-full"
            @columns={{this.lessonsPlacementTestData.columns}}
            @rows={{this.lessonsPlacementTestData.rows}}
            as |bt rows|
          >
            {{#if rows}}
              <bt.body as |b|>
                <b.row class="border-ms-green-150 border-b print:border-black" />
              </bt.body>
            {{else}}
              <div class="border-ms-green-150 border-b p-4 print:border-black">
                {{this.emptyText}}
              </div>
            {{/if}}
          </BasicTable>

          {{! Section header }}
          <div class="border-ms-green-150 mt-3 flex items-end justify-between border-b-4 font-bold print:border-black">
            <span class="px-3 pt-6 pb-3">
              {{t "reporting.maths.studentSummary.mathsLessons.recentActivityTitle"}}
            </span>
            <span class="w-24 px-3 pt-6 pb-3 text-center">
              {{t "reporting.maths.studentSummary.mathsLessons.recentActivitySubtitle"}}
            </span>
          </div>

          {{! Table }}
          <BasicTable
            exportable="{{t 'reporting.maths.studentSummary.mathsLessons.title'}} - {{t
              'reporting.maths.studentSummary.mathsLessons.recentActivityTitle'
            }}"
            class="w-full"
            @columns={{this.lessonsActivityData.columns}}
            @rows={{this.lessonsActivityData.rows}}
            as |bt rows|
          >
            {{#if rows}}
              <bt.body as |b|>
                <b.row class="border-ms-green-150 border-b print:border-black" />
              </bt.body>
            {{else}}
              <div class="border-ms-green-150 border-b p-4 print:border-black">
                {{this.emptyText}}
              </div>
            {{/if}}
          </BasicTable>
        </StudentDashboardTile>
        <StudentDashboardTile
          data-test-student-dashboard-tile="areas-of-weakness"
          class="m-3"
          @theme={{this.tileTheme}}
          @title={{t "reporting.maths.studentSummary.areasOfWeakness.title"}}
          @icon="phosphor-chart-line-bold"
        >
          {{! Section header }}
          <div class="border-ms-green-150 flex items-end justify-between border-b-4 font-bold print:border-black">
            <span class="px-3 pt-6 pb-3">{{t
                "reporting.maths.studentSummary.areasOfWeakness.recentActivityTitle"
              }}</span>
            <span class="w-24 px-3 pt-6 pb-3 text-center">{{t
                "reporting.maths.studentSummary.areasOfWeakness.recentActivitySubtitle"
              }}</span>
          </div>
          {{! Table }}
          <BasicTable
            exportable="{{t 'reporting.maths.studentSummary.areasOfWeakness.title'}} - {{t
              'reporting.maths.studentSummary.areasOfWeakness.recentActivityTitle'
            }}"
            class="w-full"
            @columns={{this.areasOfWeaknessRecentActivityData.columns}}
            @rows={{this.areasOfWeaknessRecentActivityData.rows}}
            as |bt rows|
          >
            {{#if rows}}
              <bt.body as |b|>
                <b.row class="border-ms-green-150 border-b print:border-black" />
              </bt.body>
            {{else}}
              <div class="border-ms-green-150 border-b p-4 print:border-black">
                {{this.emptyText}}
              </div>
            {{/if}}
          </BasicTable>
        </StudentDashboardTile>
        <StudentDashboardTile
          data-test-student-dashboard-tile="driving-tests"
          class="m-3"
          @theme={{this.tileTheme}}
          @title={{t "reporting.maths.studentSummary.drivingTests.title"}}
          @icon="phosphor-flag-checkered-fill"
        >
          {{! Section header }}
          <div class="border-ms-green-150 flex items-end justify-between border-b-4 font-bold print:border-black">
            <span class="px-3 pt-6 pb-3">
              {{t "reporting.maths.studentSummary.drivingTests.progressTitle"}}
            </span>
          </div>

          {{! Table }}
          <BasicTable
            exportable="{{t 'reporting.maths.studentSummary.drivingTests.title'}} - {{t
              'reporting.maths.studentSummary.drivingTests.progressTitle'
            }}"
            class="w-full"
            @columns={{this.drivingTestsProgressData.columns}}
            @rows={{this.drivingTestsProgressData.rows}}
            @footerRows={{this.drivingTestsProgressData.footerRows}}
            as |bt rows|
          >
            {{! Only want a header if there are rows to show }}
            {{#if rows}}
              <bt.head class="bg-ms-green-50" as |h|>
                <h.row as |r|>
                  <r.cell
                    class="border-r border-white p-3 text-center font-semibold print:border-r-0 print:border-b print:border-black"
                  />
                </h.row>
              </bt.head>
            {{/if}}

            {{! One row for each progress category }}
            <bt.body as |b|>
              <b.row class="border-ms-green-150 border-b print:border-black" />
            </bt.body>

            {{! The total number of driving tests completed }}
            <bt.foot as |f|>
              <f.row class="border-ms-green-150 border-b print:border-black" />
            </bt.foot>
          </BasicTable>

          {{! Section header }}
          <div class="border-ms-green-150 mt-3 flex items-end justify-between border-b-4 font-bold print:border-black">
            <span class="px-3 pt-6 pb-3">
              {{t "reporting.maths.studentSummary.drivingTests.recentActivityTitle"}}
            </span>
            <span class="w-24 px-3 pt-6 pb-3 text-center">
              {{t "reporting.maths.studentSummary.drivingTests.recentActivitySubtitle"}}
            </span>
          </div>

          {{! Table }}
          <BasicTable
            exportable="{{t 'reporting.maths.studentSummary.drivingTests.title'}} - {{t
              'reporting.maths.studentSummary.drivingTests.recentActivityTitle'
            }}"
            class="w-full"
            @columns={{this.drivingTestsActivityData.columns}}
            @rows={{this.drivingTestsActivityData.rows}}
            as |bt rows|
          >
            {{#if rows}}
              <bt.body as |b|>
                <b.row class="border-ms-green-150 border-b print:border-black" />
              </bt.body>
            {{else}}
              <div class="border-ms-green-150 border-b p-4 print:border-black">
                {{this.emptyText}}
              </div>
            {{/if}}
          </BasicTable>
        </StudentDashboardTile>
      </div>
      <div class="w-full lg:w-1/2 print:w-full">
        <StudentDashboardTile
          data-test-student-dashboard-tile="map-quizzes"
          class="m-3"
          @theme={{this.tileTheme}}
          @title={{t "reporting.maths.studentSummary.mapQuizzes.title"}}
          @icon="phosphor-map-trifold-fill"
        >
          {{! Section header }}
          <div class="border-ms-green-150 mt-3 flex items-end justify-between border-b-4 font-bold print:border-black">
            <span class="px-3 pt-6 pb-3">
              {{t "reporting.maths.studentSummary.mapQuizzes.progressTitle"}}
            </span>
          </div>

          {{! Table }}
          <BasicTable
            exportable="{{t 'reporting.maths.studentSummary.mapQuizzes.title'}} - {{t
              'reporting.maths.studentSummary.mapQuizzes.progressTitle'
            }}"
            class="w-full"
            @columns={{this.mapQuizzesProgressData.columns}}
            @rows={{this.mapQuizzesProgressData.rows}}
            as |bt|
          >
            <bt.body as |b|>
              <b.row class="border-ms-green-150 border-b print:border-black" />
            </bt.body>
          </BasicTable>

          {{! Section header }}
          <div class="border-ms-green-150 mt-3 flex items-end justify-between border-b-4 font-bold print:border-black">
            <span class="px-3 pt-6 pb-3">
              {{t "reporting.maths.studentSummary.mapQuizzes.recentActivityTitle"}}
            </span>
            <span class="w-24 px-3 pt-6 pb-3 text-center">
              {{t "reporting.maths.studentSummary.mapQuizzes.recentActivitySubtitle"}}
            </span>
          </div>

          {{! Table }}
          <BasicTable
            exportable="{{t 'reporting.maths.studentSummary.mapQuizzes.title'}} - {{t
              'reporting.maths.studentSummary.mapQuizzes.recentActivityTitle'
            }}"
            class="w-full"
            @columns={{this.mapQuizzesRecentActivityData.columns}}
            @rows={{this.mapQuizzesRecentActivityData.rows}}
            as |bt rows|
          >
            {{#if rows}}
              <bt.body as |b|>
                <b.row class="border-ms-green-150 border-b" />
              </bt.body>
            {{else}}
              <div class="border-ms-green-150 border-b p-4 print:border-black">
                {{this.emptyText}}
              </div>
            {{/if}}
          </BasicTable>
        </StudentDashboardTile>
        <StudentDashboardTile
          data-test-student-dashboard-tile="certificates-earned"
          class="m-3"
          @theme={{this.tileTheme}}
          @title={{t "reporting.maths.studentSummary.certificatesEarned.title"}}
          @icon="phosphor-trophy-fill"
        >
          {{! Section header }}
          <div class="border-ms-green-150 border-b-4 print:border-black"></div>
          {{! Table }}
          <BasicTable
            exportable="{{t 'reporting.maths.studentSummary.certificatesEarned.title'}} - {{t
              'reporting.maths.studentSummary.certificatesEarned.progressTitle'
            }}"
            class="w-full"
            @columns={{this.certificatesEarnedData.columns}}
            @rows={{this.certificatesEarnedData.rows}}
            as |bt|
          >
            <bt.head class="bg-ms-green-50" as |h|>
              <h.row as |r|>
                <r.cell
                  class="w-1/3 border-r border-white p-3 text-center font-semibold print:border-r-0 print:border-b print:border-black"
                />
              </h.row>
            </bt.head>
            <bt.body as |b|>
              <b.row class="border-ms-green-150 border-b print:border-black" />
            </bt.body>
          </BasicTable>
        </StudentDashboardTile>
        <StudentDashboardTile
          data-test-student-dashboard-tile="assignments"
          class="m-3"
          @theme={{this.tileTheme}}
          @title={{t "reporting.maths.studentSummary.assignments.title"}}
          @icon="phosphor-book-fill"
        >
          {{! Section header }}
          <div class="border-ms-green-150 mt-3 flex items-end justify-between border-b-4 font-bold print:border-black">
            <span class="px-3 pt-6 pb-3">
              {{t "reporting.maths.studentSummary.assignments.recentActivityTitle"}}
            </span>
            <span class="w-24 px-3 pt-6 pb-3 text-center">
              {{t "reporting.maths.studentSummary.assignments.recentActivitySubtitle"}}
            </span>
          </div>

          {{! Table }}
          <BasicTable
            exportable="{{t 'reporting.maths.studentSummary.assignments.title'}} - {{t
              'reporting.maths.studentSummary.assignments.recentActivityTitle'
            }}"
            class="w-full"
            @columns={{this.assignmentsData.columns}}
            @rows={{this.assignmentsData.rows}}
            as |bt rows|
          >
            {{#if rows}}
              <bt.body as |b|>
                <b.row class="border-ms-green-150 border-b print:border-black" />
              </bt.body>
            {{else}}
              <div class="border-ms-green-150 border-b p-4 print:border-black">
                {{this.emptyText}}
              </div>
            {{/if}}
          </BasicTable>
        </StudentDashboardTile>
        <StudentDashboardTile
          data-test-student-dashboard-tile="mental-minute"
          class="m-3"
          @theme={{this.tileTheme}}
          @title={{t "reporting.maths.studentSummary.mentalMinute.title"}}
          @icon="phosphor-chart-line-bold"
          @tooltipTitle={{t "reporting.maths.studentSummary.mentalMinute.tooltipTitle"}}
        >
          {{! Section header }}
          <div class="border-ms-green-150 flex items-end justify-between border-b-4 font-bold print:border-black">
            <span class="px-3 pt-6 pb-3">{{t "reporting.maths.studentSummary.mentalMinute.progressTitle"}}</span>
          </div>
          {{! Table }}
          <BasicTable
            exportable="{{t 'reporting.maths.studentSummary.mentalMinute.title'}} - {{t
              'reporting.maths.studentSummary.mentalMinute.progressTitle'
            }}"
            class="w-full"
            @columns={{this.mentalMinuteProgressData.columns}}
            @rows={{this.mentalMinuteProgressData.rows}}
            as |bt|
          >
            <bt.body as |b|>
              <b.row class="border-b print:border-black" />
            </bt.body>
          </BasicTable>
          {{! Section header }}
          <div class="border-ms-green-150 flex items-end justify-between border-b-4 font-bold print:border-black">
            <span class="px-3 pt-6 pb-3">{{t "reporting.maths.studentSummary.mentalMinute.recentActivityTitle"}}</span>
            <span class="w-40 px-3 pt-6 pb-3 text-center">{{t
                "reporting.maths.studentSummary.mentalMinute.recentActivitySubtitle"
              }}</span>
          </div>
          {{! Table }}
          <BasicTable
            exportable="{{t 'reporting.maths.studentSummary.mentalMinute.title'}} - {{t
              'reporting.maths.studentSummary.mentalMinute.recentActivityTitle'
            }}"
            class="w-full"
            @columns={{this.mentalMinuteRecentActivityData.columns}}
            @rows={{this.mentalMinuteRecentActivityData.rows}}
            as |bt rows|
          >
            {{#if rows}}
              <bt.body as |b|>
                <b.row class="border-ms-green-150 border-b print:border-black" />
              </bt.body>
            {{else}}
              <div class="border-ms-green-150 border-b p-4 print:border-black">
                {{this.emptyText}}
              </div>
            {{/if}}
          </BasicTable>
        </StudentDashboardTile>
      </div>
    </div>
  </template>
}

export default RouteTemplate(UiScopeMathsStudentSummaryTablePage)
